import { useQuery } from "@tanstack/react-query"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { CodeDetailsDialog } from "@/components/code-details-dialog"
import { HistoryFilters } from "@/components/history/history-filters"
import { HistoryTable } from "@/components/history/history-table"
import { AppLayout } from "@/components/app-layout"
import type { Database } from "@/integrations/supabase/types"

type CollectionCode = Database["public"]["Tables"]["collection_codes"]["Row"]

const History = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "sent" | "cancelled" | "used">("all")
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCode, setSelectedCode] = useState<CollectionCode | null>(null)

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error || !session) {
        console.error("Authentication error:", error)
        toast({
          title: "Authentification requise",
          description: "Veuillez vous connecter pour accéder à cette page",
          variant: "destructive",
        })
        navigate("/login")
        return
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [navigate, toast])

  const { data: codes, error, refetch } = useQuery({
    queryKey: ['collection-codes', searchTerm, statusFilter],
    queryFn: async () => {
      console.log("Fetching collection codes...")
      let query = supabase
        .from('collection_codes')
        .select('*')
        .order('sent_at', { ascending: false, nullsFirst: true })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      if (searchTerm) {
        query = query.or(`client_name.ilike.%${searchTerm}%,key_name.ilike.%${searchTerm}%`)
      }

      const { data, error } = await query
      
      if (error) {
        console.error("Error fetching codes:", error)
        throw error
      }

      console.log("Codes fetched successfully:", data)
      return data
    },
    enabled: !isLoading
  })

  const handleCancel = async (code: string) => {
    try {
      console.log("Cancelling code:", code)
      
      const { error: keynestError } = await supabase.functions.invoke('delete-keynest-code', {
        body: { code }
      })

      if (keynestError) throw keynestError

      const { error: updateError } = await supabase
        .from('collection_codes')
        .update({ status: 'cancelled' })
        .eq('code', code)

      if (updateError) throw updateError

      toast({
        title: "Code annulé",
        description: "Le code a été annulé avec succès",
      })

      refetch()
    } catch (error) {
      console.error("Error cancelling code:", error)
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'annulation du code",
        variant: "destructive",
      })
    }
  }

  const handleSendNow = async (code: string) => {
    try {
      console.log("Sending code now:", code)
      
      const { data: codeData, error: fetchError } = await supabase
        .from('collection_codes')
        .select('*')
        .eq('code', code)
        .single()

      if (fetchError) throw fetchError

      if (codeData.client_email) {
        await supabase.functions.invoke('send-code-email', {
          body: {
            clientName: codeData.client_name,
            clientEmail: codeData.client_email,
            code: codeData.code,
            keyName: codeData.key_name
          }
        })
      }

      if (codeData.client_phone) {
        await supabase.functions.invoke('send-code-sms', {
          body: {
            clientName: codeData.client_name,
            clientPhone: codeData.client_phone,
            code: codeData.code,
            keyName: codeData.key_name
          }
        })
      }

      const { error: updateError } = await supabase
        .from('collection_codes')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('code', code)

      if (updateError) throw updateError

      toast({
        title: "Code envoyé",
        description: "Le code a été envoyé avec succès",
      })

      refetch()
    } catch (error) {
      console.error("Error sending code:", error)
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du code",
        variant: "destructive",
      })
    }
  }

  if (isLoading) {
    return <div>Chargement...</div>
  }

  if (error) {
    return <div>Erreur lors du chargement des codes</div>
  }

  return (
    <AppLayout title="Historique des codes">
      <HistoryFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      <HistoryTable
        codes={codes}
        onCancel={handleCancel}
        onSendNow={handleSendNow}
        onShowDetails={setSelectedCode}
      />

      <div className="mt-4">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">1</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext href="#" />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      <CodeDetailsDialog 
        isOpen={selectedCode !== null}
        onClose={() => setSelectedCode(null)}
        code={selectedCode}
      />
    </AppLayout>
  )
}

export default History
